import { CeruleanButton } from 'components/Button'
import Field from 'components/Field'
import Input from 'components/Input'
import InputContainer from 'components/InputContainer'
import Text from 'components/Text'
import formName from 'constants/formName'
import { actions, selectors } from 'data'
import { CreateApiKeyFormType } from 'data/Form/ApiKey/types'
import messages from 'lang'
import React, { useEffect } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { FormSection, InjectedFormProps, reduxForm } from 'redux-form'
import styled from 'styled-components'
import { validator } from 'utils'

import Container from '../../Modal/Container'
import FirstSetup2FA from '../FirstSetup2FA'

const Label = styled(Text)`
  margin-bottom: 0.75rem;
`

const RadioInputContainer = styled(InputContainer)`
  margin-bottom: 0;
`

type NotFormProps = WrappedComponentProps
type Props = InjectedFormProps<CreateApiKeyFormType, NotFormProps> & NotFormProps

const CreateApiKey = ({ handleSubmit, intl, valid }: Props) => {
  const dispatch = useDispatch()
  const twoFactorStatus = useSelector(selectors.auth.getTwoFactorStatus)
  const has2FASetup = twoFactorStatus && twoFactorStatus.length > 0
  const needsChallenge = has2FASetup && twoFactorStatus!.includes('WALLET_SMS')

  useEffect(() => {
    dispatch(actions.twoFactor.getStatus())
  }, [])

  useEffect(() => {
    if (needsChallenge) {
      dispatch(actions.auth.secondFactorChallenge.createChallenge())
    }
  }, [needsChallenge])

  return (
    <form onSubmit={handleSubmit}>
      <Container title={intl.formatMessage(messages.title_create_api_key)}>
        <FormSection name='metadata'>
          <InputContainer>
            <Field
              component={Input}
              data-lpignore='true'
              e2e='nameInput'
              label={intl.formatMessage(messages.label_name)}
              name='tokenName'
              type='text'
              validate={[validator.required]}
              variant='createApiKey'
            />
          </InputContainer>

          <Label color='grey600' size='xs' weight={600}>
            {intl.formatMessage(messages.label_permissions)}
          </Label>

          <RadioInputContainer>
            <Field
              component='input'
              data-lpignore='true'
              e2e='readPermissionInput'
              label={intl.formatMessage(messages.label_read)}
              name='tokenType'
              type='radio'
              value='VIEW'
              variant='createApiKey'
            />
          </RadioInputContainer>
          <InputContainer>
            <Field
              component='input'
              data-lpignore='true'
              e2e='writePermissionInput'
              label={intl.formatMessage(messages.label_write)}
              name='tokenType'
              type='radio'
              value='TRADE'
              variant='createApiKey'
            />
            <Field
              component='input'
              data-lpignore='true'
              e2e='writePermissionInput'
              label={intl.formatMessage(messages.label_full)}
              name='tokenType'
              type='radio'
              value='FULL'
              variant='createApiKey'
            />
          </InputContainer>
          <InputContainer>
            <Field
              component={Input}
              data-lpignore='true'
              e2e='ipWhitelistInput'
              label={intl.formatMessage(messages.label_ip_whitelist)}
              name='ipAddresses'
              parse={(val) => val.split(',')}
              placeholder={intl.formatMessage(messages.placeholder_whitelist)}
              type='text'
              validate={[validator.validIpAddress]}
              variant='createApiKey'
            />
          </InputContainer>
        </FormSection>
        {has2FASetup ? (
          <InputContainer>
            <Field
              component={Input}
              data-lpignore='true'
              e2e='2faCode'
              label={intl.formatMessage(messages.label_code)}
              name='code'
              placeholder={intl.formatMessage(messages.title_verify_2fa)}
              type={twoFactorStatus!.includes('WALLET_YUBIKEY') ? 'password' : 'text'}
              validate={[validator.required]}
              variant='createApiKey'
            />
          </InputContainer>
        ) : (
          <FirstSetup2FA />
        )}

        <CeruleanButton
          disabled={!valid || !has2FASetup}
          e2e='createApiKeyButtonSubmit'
          type='submit'
        >
          {intl.formatMessage(messages.button_next)}
        </CeruleanButton>
      </Container>
    </form>
  )
}

const FormWrapper = reduxForm<CreateApiKeyFormType, NotFormProps>({
  form: formName.API_KEY
})(CreateApiKey)
export default injectIntl(FormWrapper)
